// Register.jsx
// Styled to match the KinetiQ design system (Login.css tokens + Navbar).
// Layout: same two-panel split as Login —
//   LEFT  → dark gradient hero with value-prop copy
//   RIGHT → white card with the registration form
// Uses shared <Navbar activePage="register" /> for consistent branding.

import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/Register.css';
import Navbar from './Navbar';
import Footer from './Footer';

// Role descriptions — kept only for reference in copy; every new user is
// now auto-assigned this role, so there's nothing here for them to pick.
const DEFAULT_ROLE = 'AssociateResearcher';

// const RESTRICTED_NOTE =
//   'Every account is created as an Associate Researcher, with access to the ' +
//   'Methyl Acetate (MeOAc) and Dimethyl Ether (DME) reaction systems. Need the ' +
//   'Acetic Acid (AA) module? Use the "Request Access" button inside the Data ' +
//   'Fitting page after signing in.';

const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name:       '',
    last_name:        '',
    email:            '',
    password:         '',
    confirm_password: '',
    organization:     '',
    role:             DEFAULT_ROLE,
  });

  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const response = await axios.post('https://www.kinetiqmodel.com/api/register', form);
      if (response.data.message) setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Registration failed. Please try again.'
      );
    }
  };

  return (
    <div className="reg-shell">

      {/* ── Shared Navbar ──────────────────────────────────────────────────
          activePage="register" → shows logo + "Already have an account?
          Sign in" ghost link only.                                        */}
      <Navbar activePage="register" />

      {/* ── Two-column layout ──────────────────────────────────────────── */}
      <div className="reg-page">

        {/* LEFT PANEL — dark gradient, same as Login */}
        <div className="reg-left">
          <div className="reg-hero-copy">

            <span className="reg-badge">
              KinetiQ &middot; Web-based kinetics platform
            </span>

            <h1>
              Join researchers already fitting{' '}
              <em>catalytic reaction kinetics</em>
              {' '}in the cloud.
            </h1>

            <p>
              Create your free KinetiQ account and start uploading
              experimental data, configuring reaction networks, and
              exporting publication-ready parameters -all in your browser.
            </p>

            {/* Feature checklist — same style as Login left panel */}
            <ul className="reg-feature-list">
              {[
                'Free account, no credit card required',
                'ODE-based parameter fitting',
                'Global multi-temperature fits',
                'Share results with a link',
              ].map((item) => (
                <li key={item}>
                  <span className="check">✓</span>
                  {item}
                </li>
              ))}
            </ul>

          </div>

          {/* Web pill at bottom */}
          <div className="reg-web-pill">
            <span className="pill-icon">🌐</span>
            Runs in any modern browser -nothing to download or install
          </div>
        </div>

        {/* RIGHT PANEL — registration form */}
        <div className="reg-right">
          <div className="reg-card">

            {/* ── Success state ── */}
            {success ? (
              <div className="reg-success">
                <div className="reg-success-icon">✅</div>
                <h2 className="reg-success-title">Account Created!</h2>
                <p className="reg-success-text">
                  Your account has been created successfully. You can now sign
                  in.
                </p>
                <button
                  className="reg-submit-btn"
                  onClick={() => navigate('/login')}
                >
                  Go to Sign In →
                </button>
              </div>
            ) : (
              <>
                {/* Card header */}
                <div className="reg-card-header">
                  <span className="reg-card-label">Create Account</span>
                  <h2>Get started for free</h2>
                  <p>Set up your KinetiQ account in under a minute.</p>
                </div>

                {/* Restricted access info banner */}
                {/* <div className="reg-info-banner">
                  <span className="reg-info-icon">🔒</span>
                  <p>{RESTRICTED_NOTE}</p>
                </div> */}

                {/* Error banner */}
                {error && (
                  <div className="reg-error" role="alert">
                    {error}
                  </div>
                )}

                {/* Form */}
                <form className="reg-form" onSubmit={handleRegister}>

                  {/* Name row */}
                  <div className="reg-name-row">
                    <div className="reg-form-group">
                      <label htmlFor="reg-first">First name</label>
                      <input
                        id="reg-first"
                        type="text"
                        name="first_name"
                        placeholder="First name"
                        value={form.first_name}
                        onChange={handleChange}
                        required
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="reg-form-group">
                      <label htmlFor="reg-last">Last name</label>
                      <input
                        id="reg-last"
                        type="text"
                        name="last_name"
                        placeholder="Last name"
                        value={form.last_name}
                        onChange={handleChange}
                        required
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  <div className="reg-form-group">
                    <label htmlFor="reg-email">Email address</label>
                    <input
                      id="reg-email"
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="reg-form-group">
                    <label htmlFor="reg-org">
                      Organization name{' '}
                      <span className="reg-optional">(optional)</span>
                    </label>
                    <input
                      id="reg-org"
                      type="text"
                      name="organization"
                      placeholder="University, company or lab"
                      value={form.organization}
                      onChange={handleChange}
                      maxLength={255}
                      autoComplete="organization"
                    />
                  </div>

                  <div className="reg-form-group">
                    <label htmlFor="reg-password">Password</label>
                    <input
                      id="reg-password"
                      type="password"
                      name="password"
                      placeholder="Create a password"
                      value={form.password}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="reg-form-group">
                    <label htmlFor="reg-confirm">Confirm password</label>
                    <input
                      id="reg-confirm"
                      type="password"
                      name="confirm_password"
                      placeholder="Repeat your password"
                      value={form.confirm_password}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                    />
                  </div>

                  {/* Role is auto-assigned (DEFAULT_ROLE) — no selector
                      shown. See reg-info-banner above for what that
                      default role grants access to. */}

                  <button type="submit" className="reg-submit-btn">
                    Create Account →
                  </button>

                </form>

                {/* Sign-in link */}
                <div className="reg-divider">or</div>
                <p className="reg-login-row">
                  Already have an account?{' '}
                  <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>
                    Sign in
                  </a>
                </p>

              </>
            )}

          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default Register;